// libs
import React, { memo, useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import Image from 'shared/components/ImageV2';
import ChunkFallback from 'shared/components/ChunkFallback';
import ContentWrapper from './ContentWrapper';

// contexts
import { useResponsiveContext } from 'components/ResponsiveLoader/ResponsiveContext';

// styles
import {
  APluContainer,
  Placeholder,
  ViewMoreInfoCTA,
  ViewMoreIconContainer,
  ViewMoreInfoText,
  RippleItWrapper,
} from './styles';
import { BASE_GUTTER_SPACE, COLORS } from 'shared/styles/base';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// services
import { getAPlusContent as _getAPlusContent } from 'shared/store/details/services';

// constants
import { HEADING, BANNER_ASPECT_RATION, VIEW_MORE_INFO } from './constants';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// helpers
import { log } from 'shared/helpers/newRelic';

//hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

const VIEW_MORE_ICON_SIZE = '20px';
interface IAPlusContentProps {
  heroImageURL: string;
  getAPlusContent: typeof _getAPlusContent;
  productId: string;
  content: string;
  isError: boolean;
  isFetched: boolean;
  className?: string;
}

function APlusContent({
  heroImageURL,
  getAPlusContent,
  productId,
  content,
  isError,
  isFetched,
  className = '',
}: IAPlusContentProps) {
  const [imageWidth, setImageWidth] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const { deviceWidth } = useResponsiveContext();
  const showAPlusContent = useRemoteConfig('showAPlusContent');
  const ViewMoreIcon = useStoreIcon(IconName.CHEVRON_DOWN_SMALL);

  useEffect(() => {
    setImageWidth(deviceWidth - BASE_GUTTER_SPACE * 10);
  }, [deviceWidth]);

  if (isEmpty(heroImageURL) || !showAPlusContent) {
    return null;
  }

  const openLink = async () => {
    const isEmptyAPlusContent = isEmpty(content);

    // check if data isn't already fetched
    if (isEmptyAPlusContent && isError === false && isFetched === false) {
      let data = {};

      try {
        data = await getAPlusContent(productId);
      } catch (error) {
        log({
          errTitle: 'Error in getting A Plus content - Mobile',
          error,
          tags: { [TAG_IDENTIFIER.MODULE]: 'A_PLUS_CONTENT' },
        });
      }

      if (isEmpty(data) || isEmpty(data.response.product.aplus_description)) {
        return;
      }
    }

    // if data is already fetched and is empty
    if (isEmptyAPlusContent && isError === false && isFetched === true) {
      return;
    }

    // if request has failed in the past
    if (isEmptyAPlusContent && isError === true) {
      return;
    }

    setShowOverlay(true);
  };

  const onOverlayHide = () => {
    setShowOverlay(false);
  };

  return (
    <APluContainer className={className}>
      {imageWidth === 0 ? (
        <Placeholder />
      ) : (
        <RippleItWrapper onClick={openLink}>
          <Image
            src={heroImageURL}
            width={imageWidth}
            aspectRatio={BANNER_ASPECT_RATION}
            alt={HEADING}
            data-test="APlusContent-banner-image"
            placeholderColor={COLORS.PLACEHOLDER}
          />
        </RippleItWrapper>
      )}

      <ViewMoreInfoCTA data-test="view-more" onClick={openLink}>
        <ViewMoreInfoText>{VIEW_MORE_INFO}</ViewMoreInfoText>
        <ViewMoreIconContainer>
          <ViewMoreIcon size={VIEW_MORE_ICON_SIZE} />
        </ViewMoreIconContainer>
      </ViewMoreInfoCTA>

      {showOverlay && <ContentWrapper onOverlayHide={onOverlayHide} fallback={<ChunkFallback />} />}
    </APluContainer>
  );
}

export default memo(APlusContent);
